import React__default, { useState, useEffect } from 'react';
import DayPicker from 'react-day-picker';
import { useClickOutside } from '../../../hooks/useClickOutside.es.js';
import '../../../theme/Colors.es.js';
import '../../../theme/Shadows.es.js';
import '../../../theme/Typography.es.js';
import { Zindexes } from '../../../theme/Zindexes.es.js';
import { LegacyButton } from '../../navigation/LegacyButton/Button.es.js';
import { ButtonNav } from '../../navigation/ButtonNav/ButtonNav.es.js';
import { FloatingBox } from '../../display/FloatingBox/FloatingBox.es.js';
import { DateRangePickerInput } from './DateRangePickerInput.es.js';
import { DateRangePickerNavbar } from './DateRangePickerNavbar.es.js';
import { isSameDay, calculateNextRange } from './utils.es.js';

const root = "_root_ympit_1";
const menu = "_menu_ympit_5";
const options = "_options_ympit_14";
const doneButton = "_done-button_ympit_25";
const modalBackdrop = "_modal-backdrop_ympit_31";
const modal = "_modal_ympit_31";
const styles = {
	root: root,
	menu: menu,
	options: options,
	doneButton: doneButton,
	modalBackdrop: modalBackdrop,
	modal: modal
};

const body = "_body_iw2oj_1";
const caption = "_caption_iw2oj_5";
const container = "_container_iw2oj_12";
const day = "_day_iw2oj_48";
const interactionDisabled = "_interactionDisabled_iw2oj_63";
const dayPickerDay = "_DayPicker-Day_iw2oj_66";
const month = "_month_iw2oj_70";
const medium = "_medium_iw2oj_76";
const small = "_small_iw2oj_80";
const months = "_months_iw2oj_84";
const week = "_week_iw2oj_88";
const weekday = "_weekday_iw2oj_92";
const weekdays = "_weekdays_iw2oj_99";
const wrapper = "_wrapper_iw2oj_106";
const outside = "_outside_iw2oj_111";
const selected = "_selected_iw2oj_116";
const today = "_today_iw2oj_122";
const disabled = "_disabled_iw2oj_127";
const dayPickerStyles = {
	body: body,
	caption: caption,
	container: container,
	day: day,
	interactionDisabled: interactionDisabled,
	dayPickerDay: dayPickerDay,
	month: month,
	medium: medium,
	small: small,
	months: months,
	week: week,
	weekday: weekday,
	weekdays: weekdays,
	wrapper: wrapper,
	outside: outside,
	selected: selected,
	today: today,
	disabled: disabled
};

const defaultValue = {};
const DateRangePicker = ({ "aria-label": ariaLabel, menuAriaLabel, onChange, onDayClick = () => {
}, value: valueProp = defaultValue, placeholder = void 0, disabledDates = void 0, id = void 0, isDisabled = void 0, isErrored = void 0, closeText = "Done", options = [], menuAlign = "left", size = "medium", inputSize = "medium", variant: variantProp = void 0 }) => {
  const props = {
    "aria-label": ariaLabel,
    menuAriaLabel,
    onChange,
    onDayClick,
    value: valueProp,
    placeholder,
    disabledDates,
    id,
    isDisabled,
    isErrored,
    closeText,
    options,
    menuAlign,
    size
  };
  let variant = variantProp;
  if (!variant) {
    if (size === "small") {
      variant = "modal";
    } else {
      variant = "dropdown";
    }
  }
  const [selectedOption, setSelectedOption] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState(props.value || defaultValue);
  const { from, to } = value;
  const clampedSize = props.size === "small" ? props.size : "medium";
  const isSmall = props.size === "small";
  useEffect(() => {
    if (props.value !== value) {
      setValue(props.value);
    }
  }, [props.value]);
  const clearSelection = () => {
    setValue(defaultValue);
    props.onChange(defaultValue);
  };
  const closeMenu = () => {
    var _a, _b;
    setTimeout(() => {
      setIsOpen(false);
    }, 0);
    if (!isSameDay(from, (_a = props.value) === null || _a === void 0 ? void 0 : _a.from) || !isSameDay(to, (_b = props.value) === null || _b === void 0 ? void 0 : _b.to)) {
      props.onChange(value);
    }
  };
  const openMenu = () => {
    setIsOpen(true);
  };
  const handleDayClick = (day) => {
    onDayClick(day);
    const nextRange = calculateNextRange(day, from, to);
    setValue(nextRange);
    if (selectedOption !== null) {
      setSelectedOption(null);
    }
  };
  const handleSelectOption = (option, event) => {
    event.preventDefault();
    setSelectedOption(option);
    setValue(option.value);
  };
  const [setClickOutsideNode] = useClickOutside({
    onClickOutside: closeMenu
  });
  const menuInner = isOpen && React__default.createElement(
    "div",
    { "aria-label": props.menuAriaLabel, className: styles.menu, "data-size": props.size, "data-zds": true },
    props.options.length > 0 && React__default.createElement("div", { className: styles.options, "data-size": props.size, "data-zds": true }, props.options.map((option) => React__default.createElement(ButtonNav, { "aria-label": "Predefined date ranges", isButtonFullWidth: true, items: [option], onChooseItem: handleSelectOption, selectedItemKey: selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.key }))),
    React__default.createElement(DayPicker, { classNames: {
      body: dayPickerStyles.body,
      caption: dayPickerStyles.caption,
      container: dayPickerStyles.container,
      day: dayPickerStyles.day,
      interactionDisabled: dayPickerStyles.interactionDisabled,
      month: `${dayPickerStyles.month} ${dayPickerStyles[clampedSize]}`,
      months: dayPickerStyles.months,
      navBar: "",
      navButtonNext: "",
      navButtonPrev: "",
      navButtonInteractionDisabled: "",
      footer: "",
      todayButton: "",
      week: dayPickerStyles.week,
      weekNumber: "",
      weekday: dayPickerStyles.weekday,
      weekdays: dayPickerStyles.weekdays,
      weekdaysRow: "",
      wrapper: dayPickerStyles.wrapper,
      // Modifiers
      outside: dayPickerStyles.outside,
      selected: dayPickerStyles.selected,
      today: dayPickerStyles.today,
      disabled: dayPickerStyles.disabled
    }, disabledDays: props.disabledDates, fixedWeeks: true, initialMonth: from, modifiers: value, modifiersStyles: {
      from: {
        borderBottomLeftRadius: 10,
        borderTopLeftRadius: 10
      },
      to: {
        borderBottomRightRadius: 10,
        borderTopRightRadius: 10
      }
    }, navbarElement: DateRangePickerNavbar, numberOfMonths: isSmall ? 1 : 2, onDayClick: (day) => handleDayClick(day), selectedDays: [from, value] }),
    React__default.createElement(
      "div",
      { className: styles.doneButton, "data-zds": true },
      React__default.createElement(LegacyButton, { isFullWidth: props.size === "small", onClick: closeMenu }, props.closeText)
    )
  );
  return React__default.createElement(
    "div",
    { className: styles.root, "data-zds": true },
    React__default.createElement(DateRangePickerInput, Object.assign({}, props, { clearSelection, isOpen, onClick: () => {
      if (!props.isDisabled && !isOpen) {
        openMenu();
      }
    }, options: props.options, value, size: inputSize })),
    isOpen && (variant === "modal" ? React__default.createElement(
      "div",
      { className: styles.modalBackdrop, "data-zds": true, style: {
        "--_zds-date-range-picker-modal-backdrop-z-index": Zindexes.modalOverlay
      } },
      React__default.createElement("div", { className: styles.modal, ref: setClickOutsideNode, "data-size": props.size, "data-zds": true }, menuInner)
    ) : React__default.createElement(FloatingBox, { align: props.menuAlign, intersectHeight: 460, onClickOutside: closeMenu }, menuInner))
  );
};

export { DateRangePicker };
//# sourceMappingURL=DateRangePicker.es.js.map
